import isFinite from 'lodash/isFinite'

import handleResponse, {
  handleLoadResponse,
  handleSaveResponse,
} from './handleResponse'
import { APP_CONFIG_GET_A_NEWS_LETTER_API } from '@config/app.config'
import {
  ITEM_TYPE_MAIL,
  ITEM_TYPE_CONFIRMATION_MAIL,
  ITEM_TYPE_RESPONDER,
  ITEM_TYPE_FORM,
  ITEM_TYPE_PAGE,
  ITEM_TYPE_SURVEY,
  ITEM_TYPE_SURVEY_THANK_YOU,
  WIZARD_TYPE_FORM,
  WIZARD_TYPE_PAGE,
  WIZARD_TYPE_SURVEY,
  ITEM_TYPE_LANDING_PAGE,
  ITEM_TYPE_LANDING_PAGE_SUCCESS,
} from '@client/components/Editor/settings'

/**
 * sub-domain "editor.gantest.com" should be added in 'ALLOWED_HOSTS' and 'CORS_ORIGIN_WHITELIST'
 * sections of '/var/api/settings/develop.py' file at 'api vagrant' server.
 */

// The configured base points at /v3. A few endpoints (Intercom identity) live
// on /v4, so derive a v4 base from it.
const V4_API = APP_CONFIG_GET_A_NEWS_LETTER_API.replace('/v3', '/v4')

const getWizard = (id, type) => {
  switch (type) {
    case WIZARD_TYPE_FORM: {
      return Promise.resolve({
        ok: true,
        json: async () => {
          return {
            hash: id,
            type: WIZARD_TYPE_FORM,
            wizard: [
              {
                kind: ITEM_TYPE_FORM,
              },
              {
                kind: ITEM_TYPE_CONFIRMATION_MAIL,
              },
              {
                kind: ITEM_TYPE_PAGE,
              },
            ],
          }
        },
      })
    }
    case WIZARD_TYPE_PAGE: {
      return Promise.resolve({
        ok: true,
        json: async () => {
          return {
            hash: id,
            type: WIZARD_TYPE_PAGE,
            wizard: [
              {
                kind: ITEM_TYPE_LANDING_PAGE,
              },
              {
                kind: ITEM_TYPE_LANDING_PAGE_SUCCESS,
              },
              {
                kind: ITEM_TYPE_CONFIRMATION_MAIL,
              },
              {
                kind: ITEM_TYPE_PAGE,
              },
            ],
          }
        },
      })
    }
    case WIZARD_TYPE_SURVEY: {
      return Promise.resolve({
        ok: true,
        json: async () => {
          return {
            hash: id,
            type: WIZARD_TYPE_SURVEY,
            wizard: [
              {
                kind: ITEM_TYPE_SURVEY,
              },
              {
                kind: ITEM_TYPE_SURVEY_THANK_YOU,
              },
            ],
          }
        },
      })
    }
    default: {
      return Promise.resolve(null)
    }
  }
}

const getContactAttributes = () => {
  return fetch(`${APP_CONFIG_GET_A_NEWS_LETTER_API}/attributes/`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  })
}

const getUserProfile = () => {
  return fetch(`${APP_CONFIG_GET_A_NEWS_LETTER_API}/profile/`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  })
}

const getUser = () => {
  return fetch(`${APP_CONFIG_GET_A_NEWS_LETTER_API}/user/`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  })
}

// v4/user/ exposes `Profile.hash` as its `id`, used as the Intercom user_id.
const getUserV4 = () => {
  return fetch(`${V4_API}/user/`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  })
}

const getMail = (wizardType) => (id) => {
  if (wizardType === WIZARD_TYPE_FORM) {
    return fetch(`${APP_CONFIG_GET_A_NEWS_LETTER_API}/forms/${id}/mail/`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    })
  }
  if (wizardType === WIZARD_TYPE_PAGE) {
    return fetch(`${APP_CONFIG_GET_A_NEWS_LETTER_API}/pages/${id}/mail/`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    })
  }
  return fetch(
    `${APP_CONFIG_GET_A_NEWS_LETTER_API}/mails/drafts/${id}/?type=2`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )
}

const updateMail = (wizardType) => (item) => {
  if (wizardType === WIZARD_TYPE_FORM) {
    return fetch(
      `${APP_CONFIG_GET_A_NEWS_LETTER_API}/forms/${item.hash}/mail/`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(item),
      }
    )
  }
  if (wizardType === WIZARD_TYPE_PAGE) {
    return fetch(
      `${APP_CONFIG_GET_A_NEWS_LETTER_API}/pages/${item.hash}/mail/`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(item),
      }
    )
  }
  return fetch(
    `${APP_CONFIG_GET_A_NEWS_LETTER_API}/mails/drafts/${item.id}/?type=2`,
    {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(item),
    }
  )
}

const getResponder = (id) => {
  return fetch(`${APP_CONFIG_GET_A_NEWS_LETTER_API}/mails/responders/${id}/`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  })
}

const updateResponder = (item) => {
  return fetch(
    `${APP_CONFIG_GET_A_NEWS_LETTER_API}/mails/responders/${item.id}/`,
    {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(item),
    }
  )
}

const getForm = (id) => {
  if (id && !isFinite(Number(id))) {
    return fetch(`${APP_CONFIG_GET_A_NEWS_LETTER_API}/forms/${id}/form/`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    })
  }
  return fetch(`${APP_CONFIG_GET_A_NEWS_LETTER_API}/mails/responders/${id}/`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  })
}

const updateForm = (item) => {
  if (item.hash && !isFinite(Number(item.hash))) {
    return fetch(
      `${APP_CONFIG_GET_A_NEWS_LETTER_API}/forms/${item.hash}/form/`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(item),
      }
    )
  }
  return fetch(`${APP_CONFIG_GET_A_NEWS_LETTER_API}/forms/${item.id}/`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(item),
  })
}

const getLandingPage = (id) => {
  return fetch(`${APP_CONFIG_GET_A_NEWS_LETTER_API}/pages/${id}/form/`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  })
}

const updateLandingPage = (item) => {
  return fetch(`${APP_CONFIG_GET_A_NEWS_LETTER_API}/pages/${item.hash}/form/`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(item),
  })
}

const getLandingPageSuccess = (id) => {
  return fetch(
    `${APP_CONFIG_GET_A_NEWS_LETTER_API}/pages/${id}/form-success/`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )
}

const updateLandingPageSuccess = (item) => {
  return fetch(
    `${APP_CONFIG_GET_A_NEWS_LETTER_API}/pages/${item.hash}/form-success/`,
    {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(item),
    }
  )
}

const getSurvey = (id) => {
  return fetch(`${APP_CONFIG_GET_A_NEWS_LETTER_API}/surveys/${id}/form/`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  })
}

const updateSurvey = (item) => {
  return fetch(`${APP_CONFIG_GET_A_NEWS_LETTER_API}/surveys/${item.hash}/`, {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(item),
  })
}

const getSurveyThankYou = (id) => {
  return fetch(
    `${APP_CONFIG_GET_A_NEWS_LETTER_API}/surveys/${id}/thank-you-page/`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )
}

const getSurveyInfo = (id) => {
  return fetch(`${APP_CONFIG_GET_A_NEWS_LETTER_API}/surveys/${id}/`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  })
}

const updateSurveyThankYou = (item) => {
  return fetch(`${APP_CONFIG_GET_A_NEWS_LETTER_API}/surveys/${item.hash}/`, {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(item),
  })
}

const getPage = (wizardType) => (id) => {
  if (wizardType === WIZARD_TYPE_FORM) {
    return fetch(`${APP_CONFIG_GET_A_NEWS_LETTER_API}/forms/${id}/page/`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    })
  }
  if (wizardType === WIZARD_TYPE_PAGE) {
    return fetch(`${APP_CONFIG_GET_A_NEWS_LETTER_API}/pages/${id}/page/`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    })
  }
  return fetch(`${APP_CONFIG_GET_A_NEWS_LETTER_API}/pages/${id}/`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  })
}

const getLandingPageInfo = (id) => {
  return fetch(`${APP_CONFIG_GET_A_NEWS_LETTER_API}/pages/${id}/`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  })
}

const updatePage = (wizardType) => (item) => {
  if (wizardType === WIZARD_TYPE_FORM) {
    return fetch(
      `${APP_CONFIG_GET_A_NEWS_LETTER_API}/forms/${item.hash}/page/`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(item),
      }
    )
  }
  if (wizardType === WIZARD_TYPE_PAGE) {
    return fetch(
      `${APP_CONFIG_GET_A_NEWS_LETTER_API}/pages/${item.hash}/page/`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(item),
      }
    )
  }
  return fetch(`${APP_CONFIG_GET_A_NEWS_LETTER_API}/pages/${item.id}/`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(item),
  })
}

const createTemplate = (mail) => {
  return fetch(`${APP_CONFIG_GET_A_NEWS_LETTER_API}/mails/templates/json/`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(mail),
  })
}

const createBlock = (item) => {
  return fetch(`${APP_CONFIG_GET_A_NEWS_LETTER_API}/blockeditor/blocks/`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(item),
  })
}

const getBlocks = ({ search, kind }) => {
  const url = `${APP_CONFIG_GET_A_NEWS_LETTER_API}/blockeditor/blocks/?item_type=${kind}&search=${
    !!search ? search : ''
  }`
  return fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  })
}

const getBlock = (id) => {
  return fetch(
    `${APP_CONFIG_GET_A_NEWS_LETTER_API}/blockeditor/blocks/${id}/`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )
}

const deleteBlock = (id) => {
  return fetch(
    `${APP_CONFIG_GET_A_NEWS_LETTER_API}/blockeditor/blocks/${id}/`,
    {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )
}

const prepareVideoPreview = (payload) => {
  return fetch(`${APP_CONFIG_GET_A_NEWS_LETTER_API}/video_thumbnail/`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })
}

const startTrial = (planlevel) => {
  return fetch(`${APP_CONFIG_GET_A_NEWS_LETTER_API}/start_trial/`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      plan_level: planlevel,
    }),
  })
}

const getAttributes = () => {
  return fetch(`${APP_CONFIG_GET_A_NEWS_LETTER_API}/attributes/`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  })
}

const getDesignSettings = () => {
  return fetch(`${APP_CONFIG_GET_A_NEWS_LETTER_API}/design-settings/`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  })
}

const updateDesignSettings = (payload) => {
  return fetch(`${APP_CONFIG_GET_A_NEWS_LETTER_API}/design-settings/`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })
}

const methods = {
  getContactAttributes: handleResponse(
    getContactAttributes,
    'Cannot fetch contact attributes'
  ),
  getUserProfile: handleResponse(getUserProfile, 'Cannot fetch user profile'),
  getUser: handleResponse(getUser, 'Cannot fetch user'),
  // Editor startup (FE-1475): rejects with the HTTP status, or '' for a
  // network failure, so the route can tell them apart.
  loadUser: handleLoadResponse(getUser, 'Cannot fetch user'),
  getUserV4: handleResponse(getUserV4, 'Cannot fetch user'),

  getMail: (wizardType) =>
    handleResponse(getMail(wizardType), 'Cannot fetch user mail'),
  updateMail: (wizardType) =>
    handleSaveResponse(updateMail(wizardType), 'Cannot update user mail'),

  // Silent variants for background polling (FE-1354). The default handlers run
  // with returnError = false, so a failure reaches catchErrors, which redirects
  // the window on 401/404 and opens a blocking modal on 5xx. That is right for a
  // fetch the user asked for, and wrong for one on a timer: an expired session
  // would navigate away from the editor and discard unsaved in-memory work
  // without any interaction, and a flaky network would pop a modal every tick.
  // With returnError = true the caller just gets undefined and skips the tick.
  getMailSilent: (wizardType) =>
    handleResponse(getMail(wizardType), 'Cannot fetch user mail', true),
  getResponderSilent: handleResponse(
    getResponder,
    'Cannot fetch user responder',
    true
  ),

  getResponder: handleResponse(getResponder, 'Cannot fetch user responder'),
  updateResponder: handleSaveResponse(
    updateResponder,
    'Cannot update user responder'
  ),

  getPage: (wizardType) =>
    handleResponse(getPage(wizardType), 'Cannot fetch user page'),
  updatePage: (wizardType) =>
    handleSaveResponse(updatePage(wizardType), 'Cannot update user page'),

  getForm: handleResponse(getForm, 'Cannot fetch user form'),
  updateForm: handleSaveResponse(updateForm, 'Cannot update user form'),

  getLandingPage: handleResponse(
    getLandingPage,
    'Cannot fetch user landing page'
  ),
  getLandingPageInfo: handleResponse(
    getLandingPageInfo,
    'Cannot fetch user page info'
  ),
  updateLandingPage: handleSaveResponse(
    updateLandingPage,
    'Cannot update user landing page'
  ),

  getLandingPageSuccess: handleResponse(
    getLandingPageSuccess,
    'Cannot fetch user landing page success'
  ),
  updateLandingPageSuccess: handleSaveResponse(
    updateLandingPageSuccess,
    'Cannot update user landing page success'
  ),

  getSurvey: handleResponse(getSurvey, 'Cannot fetch user survey'),
  getSurveyInfo: handleResponse(getSurveyInfo, 'Cannot fetch user survey info'),
  updateSurvey: handleSaveResponse(
    updateSurvey,
    'Cannot update user landing page'
  ),

  getSurveyThankYou: handleResponse(
    getSurveyThankYou,
    'Cannot fetch user landing page success'
  ),
  updateSurveyThankYou: handleSaveResponse(
    updateSurveyThankYou,
    'Cannot update user landing page success'
  ),

  createTemplate: handleResponse(createTemplate, 'Cannot create user template'),
  // Saved content: failures go to the dialog or the drop, not the document's
  // "reconnect" dialog (FE-1488, FE-1489).
  createBlock: handleLoadResponse(createBlock, 'Cannot create block'),
  getBlocks: handleLoadResponse(getBlocks, 'Cannot fetch blocks'),
  getBlock: handleLoadResponse(getBlock, 'Cannot fetch block'),
  deleteBlock: handleLoadResponse(deleteBlock, 'Cannot delete block'),

  getWizard: handleResponse(getWizard, 'Cannot fetch wizard'),

  prepareVideoPreview: handleResponse(
    prepareVideoPreview,
    'Cannot prepare video preview',
    true
  ),

  startTrial: handleResponse(startTrial, 'Cannot start trial', true),

  getAttributes: handleResponse(getAttributes, 'Cannot get attributes'),

  getDesignSettings: handleResponse(
    getDesignSettings,
    'Cannot get design settings',
    true
  ),
  updateDesignSettings: handleResponse(
    updateDesignSettings,
    'Cannot get design settings'
  ),
}

const getFetchMethodByType = (wizardType, type) => {
  if (type === ITEM_TYPE_MAIL || type === ITEM_TYPE_CONFIRMATION_MAIL) {
    return methods.getMail(wizardType)
  }
  if (type === ITEM_TYPE_RESPONDER) {
    return methods.getResponder
  }
  if (type === ITEM_TYPE_FORM) {
    return methods.getForm
  }
  if (type === ITEM_TYPE_PAGE) {
    return methods.getPage(wizardType)
  }
  if (type === ITEM_TYPE_LANDING_PAGE) {
    return methods.getLandingPage
  }
  if (type === ITEM_TYPE_LANDING_PAGE_SUCCESS) {
    return methods.getLandingPageSuccess
  }
  if (type === ITEM_TYPE_SURVEY) {
    return methods.getSurvey
  }
  if (type === ITEM_TYPE_SURVEY_THANK_YOU) {
    return methods.getSurveyThankYou
  }
  return methods.getMail(wizardType)
}

const getFetchInfoMethodByType = (wizardType) => {
  if (wizardType === WIZARD_TYPE_PAGE) {
    return methods.getLandingPageInfo
  }
  if (wizardType === WIZARD_TYPE_SURVEY) {
    return methods.getSurveyInfo
  }
  return () => Promise.resolve({})
}

// Background-poll counterpart to getFetchMethodByType. Only the two mail types
// are polled today (FE-1354); anything else returns null and the caller skips.
const getSilentFetchMethodByType = (type) => {
  if (type === ITEM_TYPE_MAIL) {
    return methods.getMailSilent(null)
  }
  if (type === ITEM_TYPE_RESPONDER) {
    return methods.getResponderSilent
  }
  return null
}

const getUpdateMethodByType = (wizardType, type) => {
  if (type === ITEM_TYPE_MAIL || type === ITEM_TYPE_CONFIRMATION_MAIL) {
    return methods.updateMail(wizardType)
  }
  if (type === ITEM_TYPE_RESPONDER) {
    return methods.updateResponder
  }
  if (type === ITEM_TYPE_FORM) {
    return methods.updateForm
  }
  if (type === ITEM_TYPE_PAGE) {
    return methods.updatePage(wizardType)
  }
  if (type === ITEM_TYPE_LANDING_PAGE) {
    return methods.updateLandingPage
  }
  if (type === ITEM_TYPE_LANDING_PAGE_SUCCESS) {
    return methods.updateLandingPageSuccess
  }
  if (type === ITEM_TYPE_SURVEY) {
    return methods.updateSurvey
  }
  if (type === ITEM_TYPE_SURVEY_THANK_YOU) {
    return methods.updateSurveyThankYou
  }
  return methods.updateMail(wizardType)
}

export default {
  ...methods,
  getFetchMethodByType,
  getSilentFetchMethodByType,
  getUpdateMethodByType,
  getFetchInfoMethodByType,
}
